export type SelectValue = string | number | null | object
export type OptionType = SelectValue

export interface OptionsFilter {
  limit: number
  query: SelectValue
  [key: string]: unknown
}

export type OptionFunction<Item extends OptionType = OptionType> = (
  filter: OptionsFilter,
) => readonly Item[] | Promise<readonly Item[]>
export type OptionsSource = readonly OptionType[] | OptionFunction | Readonly<Record<string, OptionType>>

type SourceItem<Source> = Source extends readonly (infer Item)[]
  ? Item
  : Source extends (...args: never[]) => infer Result
    ? Awaited<Result> extends readonly (infer Item)[]
      ? Item
      : never
    : never

export type SelectItem<Source> = Source extends readonly OptionType[] | OptionFunction
  ? SourceItem<Source> extends infer Item
    ? Item extends object
      ? Item
      : Record<string, Item>
    : never
  : Record<string, SelectValue>

type MapKey<Key> = string extends Key
  ? string | number
  : Key extends number
    ? Key
    : Key extends 'Infinity' | '-Infinity'
      ? number
      : Key extends `${infer Value extends number}`
        ? `${Value}` extends Key
          ? Value
          : Key
        : Key
export type SelectOptionValue<Source, Key extends string> = Source extends readonly OptionType[] | OptionFunction
  ? SelectItem<Source> extends infer Item
    ? Key extends keyof Item
      ? Extract<Item[Key], SelectValue>
      : SelectValue
    : never
  : Extract<MapKey<keyof Source>, SelectValue>

export type SelectExternalItem<Source, Key extends string, AsObject extends boolean> = AsObject extends true
  ? SelectItem<Source>
  : SelectOptionValue<Source, Key>

export type SelectModel<
  Source,
  Key extends string,
  Multiple extends boolean,
  AsObject extends boolean,
> = Multiple extends true ? SelectExternalItem<Source, Key, AsObject>[] : SelectExternalItem<Source, Key, AsObject>

export type SelectOutput<
  Source,
  Key extends string,
  Multiple extends boolean,
  AsObject extends boolean,
  Clean extends boolean,
> = SelectModel<Source, Key, Multiple, AsObject> | (Clean extends true ? (Multiple extends true ? never : null) : never)

/** All selection operations use this shape; custom fields stay on item. */
export interface SelectOption {
  value: SelectValue
  label: string
  searchText: string
  hint?: string
  disabled: boolean
  item: Record<string, unknown>
}

export type SelectModelValue = SelectValue | readonly SelectValue[]
export type SelectItems = Record<string, unknown> | Record<string, unknown>[] | null

export interface OptionsProps {
  options?: OptionsSource
  valueProp: string
  labelProp: string
  trackProp: string
  filter?: Record<string, unknown>
}

export function isOptionItem(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isSelectValue(value: unknown): value is SelectValue {
  return value === null || typeof value === 'string' || typeof value === 'number' || isOptionItem(value)
}

export function sameOptionValue(left: SelectValue, right: SelectValue) {
  if (typeof left === 'object' || typeof right === 'object') {
    return left === right
  }
  return String(left) === String(right)
}

export function normalizeOption(
  value: OptionType,
  props: Pick<OptionsProps, 'valueProp' | 'labelProp' | 'trackProp'>,
): SelectOption {
  const item = isOptionItem(value)
    ? value
    : {
        [props.valueProp]: value,
        [props.trackProp]: value,
        [props.labelProp]: value,
      }
  const id = item[props.valueProp]
  return {
    value: isSelectValue(id) ? id : null,
    label: String(item[props.labelProp] ?? ''),
    searchText: String(item[props.trackProp] ?? ''),
    hint: typeof item.hint === 'string' ? item.hint : undefined,
    disabled: Boolean(item.disabled),
    item,
  }
}

export function modelValues(value: SelectModelValue | undefined): readonly SelectValue[] {
  return value === null || value === undefined ? [] : Array.isArray(value) ? value : [value]
}

export function externalOptionValue(option: SelectOption, valueAsObject: boolean) {
  return valueAsObject ? option.item : option.value
}
